/* eslint-disable react/static-property-placement */
import React from 'react';
import { formatDate } from '@ecs/tng-common/scripts/date-formatting';
import Image from '@ecs/tng-common/components/image/Image';
import { imageWithPrefix } from '@ecs/tng-common/scripts/helpers';
// import { Link } from 'gatsby';
import { placeholderDarker } from '@ecs/tng-common/scripts/media';
import { themes, ICategory, IAuthor } from '../helpers/interfaces';
import ROUTES from '../helpers/routes';
import {
  ifAuthorHasProfileImage,
  returnAuthorInitials,
  restrictedAuthors
} from '../helpers/author-profile-images';

type ByLineProps = {
  theme?: themes;
  author?: IAuthor;
  readTime?: number | string;
  date?: string;
  category?: ICategory;
  className?: string;
  databaseId: string | number;
  isAuthorLink?: boolean;
  editor?: IAuthor;
  authorImgLazyLoading: boolean;
};
// eslint-disable-next-line react/prefer-stateless-function

export const returnDivider = () => {
  return (
    <span
      className="px-2 mb-2 byline-bullet"
      aria-hidden="true"
    />
  );
};
export default class ByLine extends React.Component<ByLineProps> {
  static defaultProps = { theme: 'white' };

  render() {
    // Account for pulls from wp graphql where there's name property doesn't map with gatsby graphql name (e.g. latest posts pagination query)
    const getAuthorName = (author: IAuthor) => {
      let name = '';
      if (author?.firstName && author?.lastName) {
        name = `${author.firstName} ${author.lastName}`;
      }
      return name;
    };

    const returnAuthorImg = (author: IAuthor) => {
      const name = getAuthorName(author);
      return ifAuthorHasProfileImage(author?.slug || '') && name ? (
        <Image
          className="rounded-circle me-2"
          alt={`${name} profile image`}
          src={imageWithPrefix(`/profiles-byline/${author?.slug}.webp`)}
          width="32"
          height="32"
          lazyLoading={this.props.authorImgLazyLoading}
          {...(this.props.authorImgLazyLoading && { placeholder: placeholderDarker })}
        />
      ) : (
        <div className="author-initials mb-lg-0 me-2 text-white text-opacity-70">
          <p className="mb-0">{returnAuthorInitials(name)}</p>
        </div>
      );
    };
    const renderAuthorsContent = () => {
      const author = this.props?.author;
      const editor = this.props?.editor;
      if (!author || restrictedAuthors.includes(author?.slug || '')) {
        return null;
      }
      const isDarkTheme = this.props.theme === 'dark';
      const returnAuthor = (element: IAuthor, text: string) => {
        const authorImg = returnAuthorImg(element);
        const name = getAuthorName(element);
        // REPLACE WITH GATSBY LINK
        return (
          <div className="d-flex align-items-center mb-2">
            {authorImg}
            <span>
              {text}
              {this.props.isAuthorLink && element?.uri && name ? (
                <a
                  href={ROUTES.AUTH.toUrl(element?.uri)}
                  className={`text-decoration-underline-hover fw-bold mb-2 ${isDarkTheme ? 'link-white' : ''}`}>
                  {name}
                </a>
              ) : (
                <span className="fw-bold">{name}</span>
              )}
            </span>
          </div>
        );
      };
      if (editor) {
        return [returnAuthor(author, 'By '), returnAuthor(editor, 'Edited by ')];
      }
      return returnAuthor(author, 'By ');
    };

    const elements = [
      renderAuthorsContent(),
      this.props.readTime && <span className="mb-2">{`${this.props.readTime} min read`}</span>,
      this.props.date && this.props.date !== '' && (
        <span className="mb-2">{formatDate(this.props.date)}</span>
      ),
      this.props.category && (
        <a
          href={ROUTES.CATEGORY.toUrl(this.props.category.uri)}
          className={`label mb-2 ${this.props.theme === 'dark' ? 'link-white bg-white bg-opacity-10 border border-white border-opacity-20' : ''} ${this.props.theme === 'white' ? 'label-blue' : ''} ${this.props.theme === 'coconut' ? 'bg-white' : ''} py-1 px-2 text-decoration-underline-hover`}>
          {this.props.category.name}
        </a>
      )
    ].filter(Boolean);

    return (
      <div
        className={`byline byline-${this.props.theme} ${this.props.theme === 'dark' ? 'text-white' : 'text-gray'} d-flex flex-wrap align-items-center body-text-xs ${this.props.className || ''}`}>
        {elements.map((element, index) => {
          const isArray = Array.isArray(element);
          const id = this.props.databaseId;
          return (
            <React.Fragment key={`${id}-${index}-byline`}>
              {isArray ? (
                <div className="d-flex flex-wrap align-items-center w-100">
                  {element.map((subElement, subIndex) => (
                    <React.Fragment key={`${id}-${index}-${subIndex}-subelement`}>
                      {subElement}
                      {subIndex < element.length - 1 ? returnDivider() : null}
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                element
              )}
              {!isArray && index < elements.length - 1 ? returnDivider() : null}
            </React.Fragment>
          );
        })}
      </div>
    );
  }
}
